import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { FileBarChart, Download, Play, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Skeleton } from "../components/ui/skeleton";

function downloadCsv(runId, code) {
  api.get(`/report-runs/${runId}/export.csv`, { responseType: "blob" }).then((res) => {
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${code}_${runId.slice(0, 8)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }).catch(() => toast.error("Export failed"));
}

export default function Reports() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const preselect = params.get("code");
  const [catalog, setCatalog] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({ project_id: "ALL", date: "", period_start: "", period_end: "" });
  const [run, setRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => {
    api.get("/report-runs").then((r) => setHistory(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/reports/catalog").then((r) => {
      setCatalog(r.data);
      if (r.data.length) setSelected(r.data.find((c) => c.code === preselect) || r.data[0]);
    }).catch((e) => toast.error(apiError(e)));
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    loadHistory();
  }, [loadHistory, preselect]);

  if (!catalog) return <Skeleton className="h-60 w-full" />;

  const runReport = async () => {
    if (!selected) return;
    setRunning(true);
    try {
      const body = {
        project_id: filters.project_id === "ALL" ? null : filters.project_id,
        date: filters.date || null,
        period_start: filters.period_start || null,
        period_end: filters.period_end || null,
      };
      const { data } = await api.post(`/reports/${selected.code}/run`, body);
      setRun(data);
      loadHistory();
      toast.success(`${selected.code} — ${data.row_count} rows`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setRunning(false);
    }
  };

  const viewRun = async (id) => {
    try {
      const { data } = await api.get(`/report-runs/${id}`);
      setRun(data);
      const def = catalog.find((c) => c.code === data.report_code);
      if (def) setSelected(def);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const usesDate = selected?.filters.includes("date");

  return (
    <div className="space-y-6" data-testid="reports-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t("reportCenter")}</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {catalog.map((c) => (
          <button key={c.code} data-testid={`report-card-${c.code}`}
            onClick={() => { setSelected(c); setRun(null); }}
            className={`apha-card p-4 text-left transition-colors ${
              selected?.code === c.code ? "border-[#1E3A8A] ring-1 ring-[#1E3A8A]/30" : "apha-hover"
            }`}>
            <div className="flex items-center gap-2">
              <FileBarChart className="h-4 w-4 text-[#1E3A8A]" />
              <span className="text-xs font-semibold text-muted-foreground">{c.code}</span>
            </div>
            <div className="font-heading font-semibold text-sm mt-1.5">{c.name}</div>
            <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{c.description}</div>
          </button>
        ))}
      </div>

      {selected && (
        <div className="apha-card p-4 flex flex-wrap items-end gap-3" data-testid="report-filters">
          <div className="space-y-1">
            <Label className="text-xs">{t("projectLabel")}</Label>
            <Select value={filters.project_id} onValueChange={(v) => setFilters({ ...filters, project_id: v })}>
              <SelectTrigger className="w-48" data-testid="report-project-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">{t("all")}</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.key} — {p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {usesDate ? (
            <div className="space-y-1">
              <Label className="text-xs">{t("reportDate")}</Label>
              <Input type="date" data-testid="report-date-input" className="w-40" value={filters.date}
                onChange={(e) => setFilters({ ...filters, date: e.target.value })} />
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <Label className="text-xs">{t("periodFrom")}</Label>
                <Input type="date" data-testid="report-from-input" className="w-40" value={filters.period_start}
                  onChange={(e) => setFilters({ ...filters, period_start: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("periodTo")}</Label>
                <Input type="date" data-testid="report-to-input" className="w-40" value={filters.period_end}
                  onChange={(e) => setFilters({ ...filters, period_end: e.target.value })} />
              </div>
            </>
          )}
          <Button data-testid="run-report-btn" className="bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={running} onClick={runReport}>
            <Play className="h-3.5 w-3.5 mr-1.5" /> {running ? "..." : t("runReport")}
          </Button>
        </div>
      )}

      {run && (
        <div className="apha-card p-4 space-y-3" data-testid="report-preview">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="font-heading font-semibold">{run.report_code} — {run.report_name}</div>
              <div className="text-xs text-muted-foreground mt-0.5" data-testid="report-meta">
                {t("period")}: {run.period_start} → {run.period_end} · {run.row_count} rows ·
                {" "}{new Date(run.generated_at).toLocaleString()} · {run.requested_by_name}
              </div>
              {run.data.note && <div className="text-[11px] text-[#92400E] mt-0.5">{run.data.note}</div>}
            </div>
            <Button variant="outline" size="sm" data-testid="export-csv-btn"
              onClick={() => downloadCsv(run.id, run.report_code)}>
              <Download className="h-3.5 w-3.5 mr-1.5" /> {t("exportCsv")}
            </Button>
          </div>
          <div className="overflow-x-auto border rounded-md max-h-[480px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {run.data.columns.map((c) => <TableHead key={c} className="text-xs whitespace-nowrap">{c}</TableHead>)}
                </TableRow>
              </TableHeader>
              <TableBody>
                {run.data.rows.map((row, i) => (
                  <TableRow key={`${run.id}-row-${row.join("|")}-${i}`}>
                    {run.data.columns.map((c, j) => <TableCell key={`${c}-${j}`} className="text-xs align-top">{String(row[j])}</TableCell>)}
                  </TableRow>
                ))}
                {run.data.rows.length === 0 && (
                  <TableRow><TableCell colSpan={run.data.columns.length} className="text-sm text-muted-foreground">{t("noData")}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      <section>
        <h2 className="font-heading font-semibold text-base mb-3">{t("reportHistory")}</h2>
        <div className="apha-card overflow-x-auto" data-testid="report-history">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Report</TableHead><TableHead>{t("period")}</TableHead><TableHead>Rows</TableHead>
              <TableHead>{t("generatedBy")}</TableHead><TableHead>{t("statusLabel")}</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id} data-testid={`report-run-row-${h.id}`}>
                  <TableCell className="text-sm font-medium">{h.report_code} — {h.report_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{h.period_start} → {h.period_end}</TableCell>
                  <TableCell className="text-xs">{h.row_count}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {h.requested_by_name} · {new Date(h.requested_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-xs">{h.status}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7" data-testid={`view-run-${h.id}`} onClick={() => viewRun(h.id)}>
                        <Eye className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7" data-testid={`export-run-${h.id}`}
                        onClick={() => downloadCsv(h.id, h.report_code)}>
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {history.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-sm text-muted-foreground">{t("noData")}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}
